import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import Cookies from 'js-cookie';
import './Groups.css';

function EditGroup() {
    const { id, type } = useParams(); // Add type parameter to identify group model
    const navigate = useNavigate();
    const [cookies] = useCookies(['csrftoken']);
    
    const [group, setGroup] = useState({ 
        name: '', 
        grade_level: '', 
        teacher: '',
        subject: '', // For core classes
        capacity: '', // For homeroom
        room_number: '' // For homeroom
    });
    const [gradeLevels, setGradeLevels] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [coreClasses, setCoreClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [groupType, setGroupType] = useState(type || 'core'); // Get from URL params

    // Group type options for display
    const groupTypeLabels = {
        'core': 'Core Class Group',
        'cte': 'CTE Group',
        'homeroom': 'Homeroom Group'
    };

    useEffect(() => {
        // Determine API endpoints based on group type
        let groupEndpoint = '';
        let teacherEndpoint = '/api/teachers/';
        
        if (groupType === 'core') {
            groupEndpoint = `/api/groups/core-groups/edit/${id}/`;
            teacherEndpoint = '/api/core-teachers/';
        } else if (groupType === 'cte') {
            groupEndpoint = `/api/groups/cte-groups/edit/${id}/`;
            teacherEndpoint = '/api/cte-teachers/';
        } else if (groupType === 'homeroom') {
            groupEndpoint = `/api/groups/homeroom-groups/edit/${id}/`;
            teacherEndpoint = '/api/teachers/';
        }

        // Fetch group data
        axios.get(groupEndpoint)
            .then(response => {
                const groupData = response.data;
                setGroup({
                    name: groupData.name || '',
                    grade_level: groupData.grade_level || '',
                    teacher: groupData.teacher || groupData.cte_teacher || groupData.homeroom_teacher || '',
                    subject: groupData.core_class || '',
                    capacity: groupData.capacity || '',
                    room_number: groupData.room_number || ''
                });
            })
            .catch(error => {
                console.error('Error fetching group:', error);
                alert('Error loading group data. Please try again.');
            });

        // Fetch grade levels
        axios.get('/courses/api/grade_levels/')
            .then(response => setGradeLevels(response.data))
            .catch(error => console.error('Error fetching grade levels:', error));

        // Fetch core classes if needed
        if (groupType === 'core') {
            axios.get('/api/core-classes/')
                .then(response => setCoreClasses(response.data))
                .catch(error => console.error('Error fetching core classes:', error));
        }

        // Fetch appropriate teachers
        axios.get(teacherEndpoint)
            .then(response => setTeachers(response.data))
            .catch(error => console.error('Error fetching teachers:', error))
            .finally(() => setLoading(false));
    }, [id, groupType]);

    const handleChange = (e) => {
        setGroup({ ...group, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Prepare data based on group type
        let groupData = { 
            name: group.name,
            grade_level: group.grade_level
        };

        // Add type-specific fields
        if (groupType === 'core') {
            groupData.teacher = group.teacher;
            groupData.core_class = group.subject;
        } else if (groupType === 'cte') {
            groupData.cte_teacher = group.teacher;
        } else if (groupType === 'homeroom') {
            groupData.homeroom_teacher = group.teacher;
            groupData.capacity = group.capacity;
            groupData.room_number = group.room_number;
        }

        // Determine API endpoint
        let endpoint = '';
        if (groupType === 'core') {
            endpoint = `/api/groups/core-groups/edit/${id}/`;
        } else if (groupType === 'cte') {
            endpoint = `/api/groups/cte-groups/edit/${id}/`;
        } else if (groupType === 'homeroom') {
            endpoint = `/api/groups/homeroom-groups/edit/${id}/`;
        }

        axios.put(endpoint, groupData, {
            headers: {
                'X-CSRFToken': Cookies.get('csrftoken')
            }
        })
        .then(response => {
            alert(`${groupTypeLabels[groupType]} updated successfully!`);
            navigate('/groups'); // Navigate back to groups home
        })
        .catch(error => {
            console.error('Error updating group:', error);
            if (error.response) {
                alert(`Error: ${error.response.data.message || JSON.stringify(error.response.data)}`);
            } else {
                alert('An unexpected error occurred.');
            }
        });
    };

    // Render type-specific fields
    const renderTypeSpecificFields = () => {
        switch (groupType) {
            case 'core':
                return (
                    <>
                        <select
                            name="subject"
                            value={group.subject}
                            onChange={handleChange}
                            required
                            disabled={loading || coreClasses.length === 0}
                            className="group-select"
                        >
                            <option value="">Select Core Subject</option>
                            {coreClasses.map(coreClass => (
                                <option key={coreClass.id} value={coreClass.id}>
                                    {coreClass.name}
                                </option>
                            ))}
                        </select>
                        <select
                            name="teacher"
                            value={group.teacher}
                            onChange={handleChange}
                            required
                            disabled={loading || teachers.length === 0}
                            className="group-select"
                        >
                            <option value="">Select Core Teacher</option>
                            {teachers.map(teacher => (
                                <option key={teacher.id} value={teacher.id}>
                                    {teacher.first_name} {teacher.last_name}
                                </option>
                            ))}
                        </select>
                    </>
                );
            case 'cte':
                return (
                    <select
                        name="teacher"
                        value={group.teacher}
                        onChange={handleChange}
                        required
                        disabled={loading || teachers.length === 0}
                        className="group-select"
                    >
                        <option value="">Select CTE Teacher</option>
                        {teachers.map(teacher => (
                            <option key={teacher.id} value={teacher.id}>
                                {teacher.first_name} {teacher.last_name}
                            </option>
                        ))}
                    </select>
                );
            case 'homeroom':
                return (
                    <>
                        <select
                            name="teacher"
                            value={group.teacher}
                            onChange={handleChange}
                            required
                            disabled={loading || teachers.length === 0}
                            className="group-select"
                        >
                            <option value="">Select Homeroom Teacher</option>
                            {teachers.map(teacher => (
                                <option key={teacher.id} value={teacher.id}>
                                    {teacher.first_name} {teacher.last_name}
                                </option>
                            ))}
                        </select>
                        <input
                            type="number"
                            name="capacity"
                            value={group.capacity}
                            onChange={handleChange}
                            placeholder="Room Capacity"
                            min="1"
                            className="group-input"
                        />
                        <input
                            type="text"
                            name="room_number"
                            value={group.room_number}
                            onChange={handleChange}
                            placeholder="Room Number"
                            className="group-input"
                        />
                    </>
                );
            default:
                return null;
        }
    };

    if (loading) {
        return <div className="loading">Loading group data...</div>;
    }

    return (
        <div className="edit-group-container">
            <h2>Edit {groupTypeLabels[groupType]}</h2>
            <form className="edit-group-form" onSubmit={handleSubmit}>
                {/* Group Type Display (read-only) */}
                <div className="group-type-display">
                    <label>Group Type:</label>
                    <span className="group-type-label">{groupTypeLabels[groupType]}</span>
                </div>

                {/* Common Fields */}
                <input
                    type="text"
                    name="name"
                    value={group.name}
                    onChange={handleChange}
                    placeholder="Group Name"
                    required
                    className="group-input"
                />
                
                <select
                    name="grade_level"
                    value={group.grade_level}
                    onChange={handleChange}
                    required
                    disabled={loading || gradeLevels.length === 0}
                    className="group-select"
                >
                    <option value="">Select Grade Level</option>
                    {gradeLevels.map(grade => (
                        <option key={grade.id} value={grade.id}>{grade.grade}</option>
                    ))}
                </select>

                {/* Type-specific Fields */}
                {renderTypeSpecificFields()}

                <div className="form-buttons">
                    <button type="submit" className="group-submit-btn" disabled={loading}>
                        Update {groupTypeLabels[groupType]}
                    </button>
                    <button 
                        type="button" 
                        className="group-cancel-btn" 
                        onClick={() => navigate('/groups')}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}

export default EditGroup;
